/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {ChangeEvent, memo, useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import TabView from '@ion/dls/src/components/tabs/current/TabView';
import {TagsCoachingText, TagsTabKey} from './TagsCoachingText';

export interface TagsTabBarProps {
  activeTab: TagsTabKey;
  onTabChange: (tab: TagsTabKey) => void;
  assignedCount: number;
  nearbyCount: number;
  // per Figma's Tags Tab bar "with Alert" doc - active whenever an assigned
  // tag is missing/out of range ...
  hasAssignedAlert?: boolean;
  // renders the tab bar's own per-tab coaching sentence beneath it (Figma's
  // Tags Tab bar "Coaching/context Text" variant) - on by default since
  // every full Tags accordion mockup shows it; the isolated "Default"/"with
  // Alert" master-component demos show the bar alone, hence this being
  // opt-out rather than always-on ...
  showCoachingText?: boolean;
}

const TAB_KEYS: TagsTabKey[] = ['assigned', 'nearby'];

const useStyles = makeStyles(() => ({
  container : {
    boxSizing : 'border-box',
    width     : '100%'
  }
}));

// each of the bar's exactly-2 tabs gets a fixed width rather than relying
// on MUI's own `variant='fullWidth'` flex-splitting - `current/Tab.tsx`
// wraps MUI's `Tab` and doesn't forward the `fullWidth` prop `Tabs` clones
// onto its children, so that flex-splitting never actually reaches the
// rendered tab. Each tab takes half the bar minus half of TabView's own
// 16px inter-tab gap - 204px at the desktop drawer's 424px (204 + 204 + 16
// = 424), shrinking with the bar on narrower (mobile) sheets ...
const tabOverrides = () => ({
  materialRoot : {
    width : 'calc(50% - 8px)',
    flex  : 'none'
  }
});

/**
 * TagsTabBar renders the Tags accordion's Assigned/In Range tab bar
 * (ION-7463), built on `current/Tab.tsx` and `current/TabView.tsx`'s shared
 * tab-bar implementation with their new count/alert badge capability.
 *
 * ```typescript
 *
 *   import {TagsTabBar} from
 *     '@ion/dls/src/components/assetdetails/current/TagsTabBar';
 *   ...
 *
 *   <TagsTabBar activeTab='assigned'
 *     onTabChange={onTabChange}
 *     assignedCount={2}
 *     nearbyCount={3} />
 *
 *   // with the out-of-range alert dot ...
 *   <TagsTabBar activeTab='assigned'
 *     onTabChange={onTabChange}
 *     assignedCount={2}
 *     nearbyCount={3}
 *     hasAssignedAlert />
 *
 * ```
 *
 */
export const TagsTabBar = ({
  activeTab,
  onTabChange,
  assignedCount,
  nearbyCount,
  hasAssignedAlert = false,
  showCoachingText = true
}: TagsTabBarProps) => {
  const classes = useStyles();

  const tabItems = useMemo(() => ([
    {tabHeader: 'Assigned', badgeCount: assignedCount, hasAlert: hasAssignedAlert},
    {tabHeader: 'In Range', badgeCount: nearbyCount}
  ]), [assignedCount, nearbyCount, hasAssignedAlert]);

  const onChange = useCallback((event: ChangeEvent<{}>, value: number) => {
    onTabChange(TAB_KEYS[value]);
  }, [onTabChange]);

  return (
    <div className={classes.container}>
      <TabView tabItems={tabItems}
        value={TAB_KEYS.indexOf(activeTab)}
        onChange={onChange}
        variant='fullWidth'
        fullBleed
        hideTabBackground
        tabOverrides={tabOverrides} />
      {showCoachingText && <TagsCoachingText activeTab={activeTab} />}
    </div>
  );
};

export default memo(TagsTabBar);
